/**
 * 后台侧栏菜单的**唯一真源**。
 *
 * 为什么要有这个文件：以前「菜单 key」写在 `<Menu.Item key="42">`、「当前高亮」写在
 * `AdminContainer.componentDidMount` 的一串 `url.indexOf(...)` 里——同一件事两份硬编码，踩过两次：
 *
 * 1. key 与 current 不一致 ⇒ Menu 出现两个相同的 key、点进去侧栏不高亮（CHNA-2659 留下的注释）；
 * 2. 高亮用「第一个命中的前缀」⇒ 短路径会抢走长路径：`/admin/dsd_store_config` 命中了更短的
 *    `/admin/dsd`，侧栏高亮到「PIC签证」；`/admin/rescue_drysuit`、`/admin/dm` 则根本没有对应分支。
 *
 * 现在：**key 直接用路由路径**（不再需要维护数字 key），高亮用**最长前缀**匹配，菜单树与高亮都由
 * 这一份配置派生 ⇒ 新增页面只改这一处，也不会再出现重复 key。
 *
 * 分组（`children`）就是侧栏里的 SubMenu，数组顺序 = 侧栏显示顺序。
 */
export const ADMIN_MENU = [
  { title: '潜水长申请', path: '/admin/dm' },
  { title: '专长申请', path: '/admin/si' },
  { title: 'IE考试报名及教练申请', path: '/admin/owsi' },
  { title: 'IE 场次管理', path: '/admin/ie_schedule' },
  { title: 'EFRI申请', path: '/admin/efri' },
  { title: '基础美人鱼教练申请', path: '/admin/basic_mermaid' },
  { title: '美人鱼教练申请', path: '/admin/mermaid' },
  { title: '自由潜教练申请', path: '/admin/fdi' },
  { title: '进阶自由潜教练申请', path: '/admin/afdi' },
  { title: '名仕自由潜教练申请', path: '/admin/mfdi' },
  { title: 'PIC签证', path: '/admin/dsd' },
  { title: 'DMD', path: '/admin/dmd' },
  { title: '查找证书', path: '/admin/merge_certs' },
  { title: '找回账号', path: '/admin/find_account' },
  { title: '修改信息', path: '/admin/update_user_info' },
  { title: '问卷审核', path: '/admin/wenjuan' },
  { title: '其它申请', path: '/admin/pockets_apps' },
  { title: '潜店申请', path: '/admin/store_apps' },
  {
    key: 'finance',
    title: '财务',
    children: [
      { title: '创建订单', path: '/admin/create_order' },
      { title: '非常规订单', path: '/admin/non_routine_orders' },
      { title: '教材订单', path: '/admin/textbook_orders' },
      { title: '教材商品管理', path: '/admin/textbook_goods' },
      { title: '申请费订单', path: '/admin/application_fee_orders' },
      { title: '发票信息', path: '/admin/invoice_info_orders' },
      { title: '富友支付管理', path: '/admin/fuiou_payment' },
    ],
  },
  {
    // 「卡面域」的 5 个入口原来散在两处：卡面审核 / 卡面订单 / 卡面图片管理 在顶层（被「优惠券」
    // 「富友投诉处理」夹着），卡面商品 / 卡面购买顺序 藏在「价格管理」下——而后者都不是价格问题。
    // 收成一个分组，路由与页面不动，只改入口位置。
    key: 'cardface',
    title: '卡面管理',
    children: [
      { title: '卡面商品', path: '/admin/ecard_goods' },
      { title: '卡面购买顺序', path: '/admin/card_stock_sort' },
      { title: '卡面图片管理', path: '/admin/card_face_image' },
      { title: '卡面审核', path: '/admin/customized_images' },
      { title: '卡面订单', path: '/admin/ecard_order' },
    ],
  },
  { title: '优惠券', path: '/admin/ecard_coupon' },
  // 客诉时效性强（平台到期判责），放顶层而不是塞进「财务」子菜单，免得客服要展开才看得到
  { title: '富友投诉处理', path: '/admin/fuiou_complaint' },
  {
    key: 'price',
    title: '价格管理',
    children: [
      { title: '会员申请价格', path: '/admin/member_application_good' },
      { title: 'Renewal 价格', path: '/admin/renewal_price' },
      { title: '会员等级', path: '/admin/card_level' },
    ],
  },
  { title: '干衣eLearning', path: '/admin/rescue_drysuit' },
  { title: '客服查询', path: '/admin/customer_service_query' },
  { title: 'E-Learning代购管理', path: '/admin/elearning_agent' },
  { title: 'Renewal 订单', path: '/admin/renewal_order' },
  { title: 'DSD 签发看板', path: '/admin/store_dsd' },
  { title: 'DSD 数据看板', path: '/admin/dsd_dashboard' },
  { title: 'DSD 潜店配置', path: '/admin/dsd_store_config' },
  { title: '课程总监/教练训练官名录', path: '/admin/pro_directory' },
]

/** 把菜单树拍平成条目（分组只作容器，不进高亮候选） */
export const flattenMenu = (menu = ADMIN_MENU) => menu.flatMap((entry) => (entry.children ? entry.children : [entry]))

/** 全部菜单路径（即所有可高亮的路径） */
export const menuPaths = (menu = ADMIN_MENU) => flattenMenu(menu).map((item) => item.path)

/**
 * 当前 URL 命中的菜单路径：**取最长匹配**。
 * <p>
 * 例：`/admin/dsd_store_config` 同时含有 `/admin/dsd` 与 `/admin/dsd_store_config`，取后者，
 * 否则侧栏会高亮到「PIC签证」。`/admin/ecard_order/123`（订单详情）回落到列表项。
 * 未命中返回 `''`（此时侧栏不高亮任何项，比高亮到错误的项好）。
 */
export const matchMenuPath = (href = '', menu = ADMIN_MENU) => {
  const matched = menuPaths(menu).filter((path) => href.indexOf(path) !== -1)
  if (!matched.length) return ''
  return matched.reduce((longest, path) => (path.length > longest.length ? path : longest))
}

/** 命中项所在分组的 key（顶层项返回 `''`）——深链接时用它自动展开侧栏分组 */
export const matchMenuGroupKey = (href = '', menu = ADMIN_MENU) => {
  const path = matchMenuPath(href, menu)
  const group = menu.find((entry) => entry.children && entry.children.some((item) => item.path === path))
  return group ? group.key : ''
}
